import { Box, useMediaQuery } from "@mui/material";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GrantAccessSuccessDialog } from "../../../../components";
import {
  ASSIGNED_VIA_DIRECT,
  AssignedViaCell,
} from "../../../../components/AssignedViaCell";
import { DataTable } from "../../../../components/data-table/DataTable";
import { DeleteActionCell } from "../../../../components/data-table/DeleteActionCell";
import {
  ManageAccessDialog,
  type AssignedVia,
} from "../../../../components/ManageAccessDialog";
import { RemoveAccessDialog } from "../../../../components/RemoveAccessDialog";
import { useLocalization } from "../../../../hooks/useLocalization";
import { formatDateYMD } from "../../../../shared/utils/date";
import { Routes } from "../../../../types/NavigationPaths";
import { TAccessSource, TExperienceUserAccessRow } from "../../types";
import { GrantAccessToUserDialog } from "../GrantAccessToUserDialog";

const getSingleGroupSource = (
  row: TExperienceUserAccessRow,
): TAccessSource | null => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const sources: TAccessSource[] = row.sources ?? [];
  const groups: TAccessSource[] = sources.filter((s) => s.type === "GROUP");

  if (groups.length === 1) {
    return groups[0] ?? null;
  }

  return null;
};

const toAssignedVia = (row: TExperienceUserAccessRow): AssignedVia[] => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const sources: TAccessSource[] = row.sources ?? [];

  return sources.map((s) =>
    s.type === "DIRECT"
      ? { source: ASSIGNED_VIA_DIRECT, sourceId: row.userId }
      : { source: s.name, sourceId: s.id },
  );
};

const AssignedViaClickableCell: React.FC<{
  row: TExperienceUserAccessRow;
  onNavigate: (groupId: string) => void;
}> = ({ row, onNavigate }) => {
  const singleGroup = getSingleGroupSource(row);
  const clickable = !!singleGroup && !row.hasDirectAccess;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!singleGroup) return;
    onNavigate(singleGroup.id);
  };

  const assignedVia = toAssignedVia(row);

  return (
    <Box
      component={clickable ? "button" : "div"}
      onClick={clickable ? handleClick : undefined}
      sx={{
        border: 0,
        p: 0,
        background: "transparent",
        cursor: clickable ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: { xs: "flex-end", md: "flex-start" },
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        maxWidth: "100%",
      }}
    >
      <AssignedViaCell assignedVia={assignedVia} />
    </Box>
  );
};

const renderAssignedViaCell = (
  row: TExperienceUserAccessRow,
  onNavigate: (groupId: string) => void,
) => <AssignedViaClickableCell onNavigate={onNavigate} row={row} />;

const renderActionsCell = (
  ctx: CellContext<TExperienceUserAccessRow, any>,
  onManageAccess: (row: TExperienceUserAccessRow) => void,
  actionText: string,
) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const row = ctx.row.original;
    onManageAccess(row);
  };

  return <DeleteActionCell actionText={actionText} onClick={handleClick} />;
};

const UserNameCell: React.FC<{
  row: TExperienceUserAccessRow;
  onNavigate: (userId: string) => void;
}> = ({ row, onNavigate }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onNavigate(row.userId);
  };

  return (
    <Box
      component="button"
      onClick={handleClick}
      sx={{
        border: 0,
        p: 0,
        background: "transparent",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        fontSize: "inherit",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
      }}
    >
      {row.fullName}
    </Box>
  );
};

const renderUserNameCell = (
  row: TExperienceUserAccessRow,
  onNavigate: (userId: string) => void,
) => <UserNameCell onNavigate={onNavigate} row={row} />;

const EmailCell: React.FC<{ value: string }> = ({ value }) => (
  <Box
    sx={{
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "block",
    }}
    title={value}
  >
    {value}
  </Box>
);

const renderEmailCell = (value: string) => <EmailCell value={value} />;

export interface ExperienceUsersTabProps {
  releaseChannelId: string;
  experienceName: string;
  rows: TExperienceUserAccessRow[];
  isLoading: boolean;
  isExternal?: boolean;
  licenseId?: string;
  licenseExpiresAt: Date | null;
}

export const ExperienceUsersTab: React.FC<ExperienceUsersTabProps> = ({
  releaseChannelId,
  experienceName,
  rows,
  isLoading,
  isExternal = false,
  licenseId,
  licenseExpiresAt,
}) => {
  const { t } = useLocalization();
  const navigate = useNavigate();

  const isCompact = useMediaQuery("(max-width: 1150px)");

  const [selectedRow, setSelectedRow] =
    React.useState<TExperienceUserAccessRow | null>(null);

  const [isGrantAccessDialogOpen, setIsGrantAccessDialogOpen] =
    React.useState(false);

  const [isGrantAccessSuccessDialogOpen, setIsGrantAccessSuccessDialogOpen] =
    React.useState(false);

  const [removeAccessCtx, setRemoveAccessCtx] = React.useState<{
    row: TExperienceUserAccessRow;
    source: TAccessSource;
    releaseChannelId: string;
    experienceName: string;
    isExternal?: boolean;
    licenseId?: string;
  } | null>(null);

  const handleOpenManage = React.useCallback(
    (row: TExperienceUserAccessRow) => {
      setSelectedRow(row);
    },
    [],
  );

  const handleCloseManage = () => setSelectedRow(null);

  const handleCloseRemove = () => setRemoveAccessCtx(null);

  const handleSuccessModalOpen = () => {
    setIsGrantAccessSuccessDialogOpen(true);
  };

  const handleSuccessModalClose = () => {
    setIsGrantAccessSuccessDialogOpen(false);
  };

  const { companyId } = useParams();

  const handleNavigateToGroup = React.useCallback(
    (groupId: string) => {
      void navigate(`/companies/${companyId}${Routes.GROUPS}/${groupId}`);
    },
    [navigate, companyId],
  );

  const handleNavigateToUser = React.useCallback(
    (userId: string) => {
      void navigate(`/companies/${companyId}${Routes.USERS}/${userId}`);
    },
    [navigate, companyId],
  );

  const columns = React.useMemo<ColumnDef<TExperienceUserAccessRow, any>[]>(
    () => [
      // 1. Name
      {
        id: "fullName",
        header: t("experiences.usersTab.nameHeader"),
        accessorKey: "fullName",
        cell: (ctx) =>
          renderUserNameCell(ctx.row.original, handleNavigateToUser),
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: true,
        sortingFn: "text",
        size: isCompact ? 100 : 190,
        minSize: 80,
        maxSize: 303,
      },
      // 2. Email
      {
        id: "email",
        header: t("experiences.usersTab.emailHeader"),
        accessorKey: "email",
        cell: (ctx) => renderEmailCell(ctx.getValue<string>()),
        enableSorting: true,
        enableColumnFilter: false,
        enableGlobalFilter: true,
        sortingFn: "text",
        size: isCompact ? 120 : 190,
        minSize: 100,
        maxSize: 303,
        meta: {
          align: "left",
        },
      },
      // 3. Assigned via
      {
        id: "accessType",
        header: t("experiences.usersTab.assignedViaHeader"),
        accessorFn: (row) => row,
        cell: (ctx) =>
          renderAssignedViaCell(ctx.row.original, handleNavigateToGroup),
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        size: isCompact ? 100 : 190,
        minSize: 100,
        maxSize: 190,
        meta: {
          align: "right",
        },
      },
      // 4. Expiration Date
      {
        id: "expiresAt",
        header: t("experiences.usersTab.expirationDateHeader"),
        accessorFn: (row) =>
          row.longestExpiry
            ? formatDateYMD(new Date(row.longestExpiry))
            : "Unlimited",
        enableSorting: true,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        size: 160,
        minSize: 160,
        maxSize: 160,
        meta: {
          align: "left",
        },
      },
      // 5. Actions
      {
        id: "actions",
        header: t("experiences.usersTab.actionsHeader"),
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        size: 133,
        minSize: 133,
        maxSize: 133,
        cell: (ctx) =>
          renderActionsCell(
            ctx,
            handleOpenManage,
            t("experiences.usersTab.manageAccess"),
          ),
        meta: {
          align: "left",
        },
      },
    ],
    [
      handleNavigateToGroup,
      handleNavigateToUser,
      handleOpenManage,
      isCompact,
      t,
    ], // Add isCompact to dep array
  );

  const handleDirectClick = React.useCallback(() => {
    const row = selectedRow;
    if (!row) return;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sources: TAccessSource[] = row.sources ?? [];
    const directSource = sources.find((s) => s.type === "DIRECT");
    if (!directSource) return;

    setRemoveAccessCtx({
      row,
      source: directSource,
      releaseChannelId,
      experienceName,
      isExternal,
      licenseId,
    });
    handleCloseManage();
  }, [selectedRow, releaseChannelId, experienceName, isExternal, licenseId]);

  const handleActionClick = React.useCallback(() => {
    setIsGrantAccessDialogOpen(true);
  }, []);

  const handleRowClick = React.useCallback(
    (row: TExperienceUserAccessRow) => {
      handleNavigateToUser(row.userId);
    },
    [handleNavigateToUser],
  );

  const handleGrantAccessDialogClose = React.useCallback(() => {
    setIsGrantAccessDialogOpen(false);
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflowX: "auto",
        }}
      >
        <DataTable<TExperienceUserAccessRow>
          actionButtonText={t("experiences.usersTab.grantAccess")}
          actionButtonTextShort={t("experiences.grantAccessToUser.grant")}
          columns={columns}
          data={rows}
          defaultSort={[{ id: "fullName", desc: false }]}
          globalFilterPlaceholder={t(
            "experiences.grantAccessToUser.searchPlaceholder",
          )}
          isLoading={isLoading}
          onActionClick={handleActionClick}
          onRowClick={handleRowClick}
        />
      </Box>

      <ManageAccessDialog
        isOpen={!!selectedRow}
        onClose={handleCloseManage}
        onDirectClick={handleDirectClick}
        sources={selectedRow ? toAssignedVia(selectedRow) : []}
        title={experienceName}
      />

      <GrantAccessToUserDialog
        isExternal={isExternal}
        isOpen={isGrantAccessDialogOpen}
        licenseId={licenseId}
        maxDate={licenseExpiresAt}
        onClose={handleGrantAccessDialogClose}
        openSuccessModal={handleSuccessModalOpen}
        releaseChannelId={releaseChannelId}
      />

      <GrantAccessSuccessDialog
        isOpen={isGrantAccessSuccessDialogOpen}
        onClose={handleSuccessModalClose}
      />

      <RemoveAccessDialog
        ctx={removeAccessCtx}
        onClose={handleCloseRemove}
        open={!!removeAccessCtx}
      />
    </>
  );
};
